import React, { useContext, useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Copy, FileSpreadsheet, LayoutList, Plus, Printer } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { shiftsAPI, shiftTemplatesAPI } from '../../services/api';
import { confirmDialog, notify, reportLoadError } from '../../utils/notify';
import { can } from '../../utils/permissions';
import { toLocalISODate } from '../../utils/dateUtils';
import { exportToExcel } from '../../utils/excelExport';
import Modal from '../../components/Modal';
import ShiftDialog, { describe } from './ShiftDialog';
import TemplatesDialog from './TemplatesDialog';
import { addDays, dayLabel, mondayOf, timeOf, weekDays, weekLabel } from './shiftDates';
import './Shifts.css';

// The shift planner: one week (Monday to Sunday, Paramaribo time) by site or
// by guard. A shift belongs to the day it starts on; a night shift shows its
// end with "(+1)". Resource "schedules": view, create, update, delete
// (cancel: shifts are never deleted).

const guardName = (x) => `${x.first_name} ${x.last_name}`;
const roundHours = (h) => Math.round(h * 100) / 100;

const Shifts = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const canCreate = can(user, 'schedules', 'create');
  const canEdit = can(user, 'schedules', 'update');
  const canCancel = can(user, 'schedules', 'delete');

  const [monday, setMonday] = useState(() => mondayOf(toLocalISODate()));
  const [view, setView] = useState('site');
  const [siteId, setSiteId] = useState('');
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [guards, setGuards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dialog, setDialog] = useState(null); // { shift } or { defaults }
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [copied, setCopied] = useState(null); // the copy's result
  const [copying, setCopying] = useState(false);

  // bumped after a save to load again
  const [shiftsVersion, setShiftsVersion] = useState(0);
  const [templatesVersion, setTemplatesVersion] = useState(0);
  const tRef = useRef(t);
  tRef.current = t;

  const days = weekDays(monday);

  useEffect(() => {
    let cancelled = false;
    Promise.all([shiftsAPI.sites(), shiftsAPI.guards()])
      .then(([s, g]) => { if (!cancelled) { setSites(s.data.sites); setGuards(g.data.guards); } })
      .catch((error) => reportLoadError(error, tRef.current('Error loading sites and guards')));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    shiftTemplatesAPI.list()
      .then((res) => { if (!cancelled) setTemplates(res.data.templates); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading templates')));
    return () => { cancelled = true; };
  }, [templatesVersion]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    shiftsAPI.list({ from: monday, to: addDays(monday, 6), ...(siteId ? { site_id: siteId } : {}) })
      .then((res) => { if (!cancelled) setShifts(res.data.shifts); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading shifts'), { main: true }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [monday, siteId, shiftsVersion]);

  const dayOf = (shift) => shift.starts_at.slice(0, 10);
  const onDay = (list, day) => list.filter((x) => dayOf(x) === day);

  // the active sites, and any other one with shifts this week
  const siteRows = sites.filter((s) => (siteId ? String(s.id) === siteId : (s.active || shifts.some((x) => x.site_id === s.id))));
  // with a site chosen: the guards planned there; otherwise every plannable guard
  const guardRows = (() => {
    const planned = new Map(shifts.map((x) => [x.employee_id, { id: x.employee_id, first_name: x.first_name, last_name: x.last_name, employee_code: x.employee_code }]));
    if (siteId) return [...planned.values()].sort((a, b) => guardName(a).localeCompare(guardName(b)));
    const rows = [...guards];
    planned.forEach((g, id) => { if (!guards.some((x) => x.id === id)) rows.push(g); });
    return rows;
  })();

  const saved = () => { setDialog(null); setShiftsVersion((n) => n + 1); };

  const copyWeek = async () => {
    const from = addDays(monday, -7);
    const site = sites.find((s) => String(s.id) === siteId);
    const question = site
      ? t('Copy the shifts at {site} of the week of {from} into this week?', { site: site.name, from })
      : t('Copy all shifts of the week of {from} into this week?', { from });
    if (!await confirmDialog(question)) return;
    try {
      setCopying(true);
      const res = await shiftsAPI.copyWeek({ from, to: monday, ...(siteId ? { site_id: Number(siteId) } : {}) });
      setCopied(res.data);
      setShiftsVersion((n) => n + 1);
    } catch (error) {
      notify(error.response?.data?.message || t('Error copying the week'), 'error');
    } finally {
      setCopying(false);
    }
  };

  const exportWeek = () => {
    const rows = shifts.map((x) => ({
      [t('Date')]: dayOf(x),
      [t('Start')]: x.starts_at.slice(11, 16),
      [t('End')]: timeOf(x.ends_at, dayOf(x)),
      [t('Hours')]: x.hours,
      [t('Guard')]: guardName(x),
      [t('Employee ID')]: x.employee_code || '',
      [t('Client')]: x.client_name,
      [t('Site')]: x.site_name,
      [t('Notes')]: x.notes || '',
    }));
    if (!exportToExcel(rows, `shifts_${monday}`, t('Shifts'))) notify(t('No shifts to export'), 'warning');
  };

  const chip = (x, label) => (
    <button key={x.id} type="button" className="sh-chip" onClick={() => setDialog({ shift: x })}
      title={`${guardName(x)} · ${x.client_name} – ${x.site_name}${x.notes ? ` · ${x.notes}` : ''}`}>
      <span className="sh-chip__who">{label}</span>
      <span className="sh-chip__time">{x.starts_at.slice(11, 16)}–{timeOf(x.ends_at, dayOf(x))}</span>
    </button>
  );

  const addButton = (defaults) => canCreate && (
    <button type="button" className="sh-add" aria-label={t('Plan a shift on {day}', { day: dayLabel(defaults.date, locale) })}
      title={t('Plan a Shift')} onClick={() => setDialog({ defaults })}>
      <Plus aria-hidden="true" />
    </button>
  );

  const grid = (rows, head, cellShifts, defaultsFor, chipLabel, extra) => (
    <div className="sh-grid-wrap">
      <table className="sh-grid">
        <thead>
          <tr>
            <th scope="col" className="sh-grid__row-head">{view === 'site' ? t('Site') : t('Guard')}</th>
            {days.map((d) => <th key={d} scope="col" className={d === toLocalISODate() ? 'is-today' : ''}>{dayLabel(d, locale)}</th>)}
            {extra && <th scope="col" className="num">{extra.title}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row" className="sh-grid__row-head">{head(row)}</th>
              {days.map((d) => {
                const list = cellShifts(row, d);
                const short = view === 'site' && row.active && list.length < row.guards_needed;
                return (
                  <td key={d} className={short ? 'is-short' : ''}>
                    <div className="sh-cell">
                      {list.map((x) => chip(x, chipLabel(x)))}
                      {short && <span className="sh-short">{t('{planned} of {needed} guards', { planned: list.length, needed: row.guards_needed })}</span>}
                      {addButton(defaultsFor(row, d))}
                    </div>
                  </td>
                );
              })}
              {extra && <td className="num">{extra.value(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const siteOptions = sites.filter((s) => s.active || String(s.id) === siteId);

  return (
    <div className="shifts-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Shifts')}</h1>
          <p className="page-sub">{t('Who is planned where, week by week')}</p>
        </div>
        <div className="sh-head-actions">
          <button type="button" className="btn btn--secondary" onClick={() => setTemplatesOpen(true)}>
            <LayoutList aria-hidden="true" /> {t('Templates')}
          </button>
          {canCreate && (
            <button type="button" className="btn btn--primary" onClick={() => setDialog({ defaults: { date: days.includes(toLocalISODate()) ? toLocalISODate() : monday, site_id: siteId } })}>
              <Plus aria-hidden="true" /> {t('Plan a Shift')}
            </button>
          )}
        </div>
      </div>

      <div className="sh-toolbar">
        <div className="sh-week">
          <button type="button" className="btn btn--secondary btn--icon" aria-label={t('Previous week')} title={t('Previous week')} onClick={() => setMonday(addDays(monday, -7))}>
            <ChevronLeft aria-hidden="true" />
          </button>
          <span className="sh-week__label"><CalendarDays aria-hidden="true" /> {weekLabel(monday, locale)}</span>
          <button type="button" className="btn btn--secondary btn--icon" aria-label={t('Next week')} title={t('Next week')} onClick={() => setMonday(addDays(monday, 7))}>
            <ChevronRight aria-hidden="true" />
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => setMonday(mondayOf(toLocalISODate()))}>{t('This week')}</button>
        </div>
        <div className="sh-filters">
          <select id="shifts-site" className="select" aria-label={t('Site')} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">{t('All sites')}</option>
            {siteOptions.map((s) => <option key={s.id} value={s.id}>{s.client_name} – {s.name}</option>)}
          </select>
          {canCreate && (
            <button type="button" className="btn btn--secondary" onClick={copyWeek} disabled={copying}>
              <Copy aria-hidden="true" /> {t('Copy previous week')}
            </button>
          )}
          <button type="button" className="btn btn--secondary" onClick={exportWeek}>
            <FileSpreadsheet aria-hidden="true" /> {t('Export Excel')}
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => window.print()}>
            <Printer aria-hidden="true" /> {t('Print')}
          </button>
        </div>
      </div>

      <div className="tabbar" role="tablist">
        {[['site', 'By site'], ['guard', 'By guard']].map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={view === key}
            className={`tabbar__tab${view === key ? ' is-active' : ''}`} onClick={() => setView(key)}>
            {t(label)}
          </button>
        ))}
      </div>

      <p className="sh-print-title">{t('Shifts')} · {weekLabel(monday, locale)}{siteId && siteRows[0] ? ` · ${siteRows[0].client_name} – ${siteRows[0].name}` : ''}</p>

      {loading ? (
        <div className="empty">{t('Loading shifts...')}</div>
      ) : view === 'site' ? (
        siteRows.length === 0 ? (
          <div className="empty"><p>{t('No sites yet: add them under Clients & Sites')}</p></div>
        ) : grid(
          siteRows,
          (s) => (
            <>
              <span className="sh-row-name">{s.name}</span>
              <span className="sh-muted">{s.client_name} · {t('{n} guards needed', { n: s.guards_needed })}</span>
            </>
          ),
          (s, d) => onDay(shifts.filter((x) => x.site_id === s.id), d),
          (s, d) => ({ date: d, site_id: s.id }),
          guardName,
        )
      ) : guardRows.length === 0 ? (
        <div className="empty"><p>{t('No guards planned this week')}</p></div>
      ) : grid(
        guardRows,
        (g) => (
          <>
            <span className="sh-row-name">{guardName(g)}</span>
            {g.employee_code && <span className="sh-muted">{g.employee_code}</span>}
          </>
        ),
        (g, d) => onDay(shifts.filter((x) => x.employee_id === g.id), d),
        (g, d) => ({ date: d, employee_id: g.id, site_id: siteId }),
        (x) => x.site_name,
        { title: t('Hours'), value: (g) => roundHours(shifts.filter((x) => x.employee_id === g.id).reduce((sum, x) => sum + x.hours, 0)) },
      )}

      {dialog && (
        <ShiftDialog
          shift={dialog.shift}
          defaults={dialog.defaults}
          guards={guards}
          sites={sites}
          templates={templates}
          canSave={dialog.shift ? canEdit : canCreate}
          canCancel={canCancel}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}

      {templatesOpen && (
        <TemplatesDialog
          templates={templates}
          sites={sites}
          canCreate={canCreate}
          canEdit={canEdit}
          onClose={() => setTemplatesOpen(false)}
          onChanged={() => setTemplatesVersion((n) => n + 1)}
        />
      )}

      <Modal open={Boolean(copied)} title={t('Week copied')} onClose={() => setCopied(null)} size="md"
        footer={<button type="button" className="btn btn--primary" onClick={() => setCopied(null)}>{t('Close')}</button>}>
        {copied && (
          <div className="sh-copied">
            <p>{t('{n} shifts created', { n: copied.created })}</p>
            {copied.skipped.length > 0 && (
              <>
                <p>{t('{n} not copied:', { n: copied.skipped.length })}</p>
                <ul className="sh-rules">
                  {copied.skipped.map((x) => (
                    <li key={`${x.employee}${x.starts_at}`} className="sh-rule is-block">
                      {x.employee} · {x.site_name} · {x.starts_at.replace('T', ' ')}: {describe({ code: x.reason }, t)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Shifts;
